import { useState } from "react";
import type { UserApplication, VerificationStatus } from "../domain/types";
import { Button } from "../components/Button";
import {
  getIneligibilityReasons,
  isEligibleForMatching,
  matchingPoolStore,
  matchStore,
} from "../matching";
import { MediaPreview } from "./MediaPreview";
import { reviewQueueStore } from "../review/queueStore";
import { decideServerReview } from "./useReviewQueue";

export function ReviewDetail({
  app,
  onBack,
  onChanged,
  serverMode = false,
}: {
  app: UserApplication;
  onBack: () => void;
  onChanged: () => void;
  serverMode?: boolean;
}) {
  const [note, setNote] = useState(app.verification.reviewNote ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const v = app.verification;
  const pending = v.status === "pending_review";
  const values = app.valueAnswers ?? {
    loyalty: null,
    honesty: null,
    passion: null,
    commitment: null,
  };

  const decide = (status: VerificationStatus) => {
    setError(null);
    if (status === "needs_resubmit" && !note.trim()) {
      setError("Add a short reason for Needs Resubmit.");
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        if (serverMode) {
          const mapped =
            status === "approved"
              ? "approved"
              : status === "rejected"
                ? "rejected"
                : "needs_resubmit";
          const res = await decideServerReview(app.id, mapped, note.trim() || undefined);
          if (!res.ok) {
            setError(res.error);
            return;
          }
          // Mirror into local applications so matching gate + pool stay in sync.
          reviewQueueStore.updateVerificationStatus(app.id, status, note.trim() || undefined);
        } else {
          reviewQueueStore.updateVerificationStatus(app.id, status, note.trim() || undefined);
        }
        matchStore.acknowledgeApproval(app.id);
        onChanged();
        onBack();
      } finally {
        setBusy(false);
      }
    })();
  };

  const poolEligible = isEligibleForMatching({
    ...app,
    status: "verified",
    verification: { ...app.verification, status: "approved" },
  });
  const poolReasons = poolEligible
    ? []
    : getIneligibilityReasons({
        ...app,
        status: "verified",
        verification: { ...app.verification, status: "approved" },
      });
  const inPool = matchingPoolStore.has(app.id);

  const rulesOk =
    app.rules.respectful && app.rules.noSexualContent && app.rules.noDominance;
  const answerRows: { label: string; value: string }[] = [
    { label: "Name", value: app.firstName || "—" },
    { label: "Age", value: app.age || "—" },
    { label: "City", value: app.city || "—" },
    { label: "Sex", value: app.gender === "male" ? "Male" : app.gender === "female" ? "Female" : "—" },
    { label: "Email", value: app.email || "—" },
    { label: "User id", value: app.id },
    {
      label: "Christian",
      value:
        app.isChristian === true ? "Yes" : app.isChristian === false ? "No" : "—",
    },
    { label: "Rules accepted", value: rulesOk ? "All three" : "Incomplete" },
    { label: "Intent", value: app.intent || "—" },
    {
      label: "Loyalty",
      value:
        values.loyalty ||
        (app.scales.loyalty != null ? `${app.scales.loyalty}/10` : "—"),
    },
    {
      label: "Honesty",
      value:
        values.honesty ||
        (app.scales.honesty != null ? `${app.scales.honesty}/10` : "—"),
    },
    {
      label: "Passion",
      value:
        values.passion ||
        (app.scales.passion != null ? `${app.scales.passion}/10` : "—"),
    },
    {
      label: "Commitment",
      value:
        values.commitment ||
        (app.scales.commitment != null ? `${app.scales.commitment}/10` : "—"),
    },
    {
      label: "Social",
      value: app.scales.social != null ? `${app.scales.social}/10` : "—",
    },
    { label: "Why Christian", value: app.whyChristian || "—" },
    { label: "About", value: app.aboutYou || "—" },
    { label: "Looking for", value: app.lookingFor || "—" },
    {
      label: "Love ready",
      value:
        app.loveReady === true ? "Yes" : app.loveReady === false ? "Not yet" : "—",
    },
    { label: "Profession", value: app.profession || "—" },
    { label: "Hobbies", value: app.hobbies || "—" },
    {
      label: "Reading",
      value: app.reading != null ? `${app.reading}/10` : "—",
    },
  ];

  return (
    <div className="rc-detail">
      <header className="rc-detail__header">
        <button type="button" className="rc-back" onClick={onBack}>
          ← Queue
        </button>
        <div className="rc-detail__title-row">
          <div>
            <div className="rc-eyebrow">Case</div>
            <h1 className="rc-title">
              {app.firstName?.trim() || "Unnamed"}
              {app.age ? `, ${app.age}` : ""}
            </h1>
            <p className="rc-lead">
              {app.email}
              {app.city ? ` · ${app.city}` : ""}
            </p>
          </div>
          <span className={`rc-badge rc-badge--${v.status}`}>{v.status}</span>
        </div>
      </header>

      <section className="rc-section">
        <h2 className="rc-section__title">Identity media</h2>
        <div className="rc-media-grid">
          <MediaPreview asset={v.governmentId} label="Government ID" large />
          <MediaPreview asset={v.confirmationVideo} label="Confirmation video" large />
        </div>
      </section>

      <section className="rc-section">
        <h2 className="rc-section__title">Profile photos ({v.profilePhotos.length})</h2>
        <div className="rc-photo-grid">
          {v.profilePhotos.map((p, i) => (
            <MediaPreview key={p.id} asset={p} label={`Photo ${i + 1}`} />
          ))}
          {v.profilePhotos.length === 0 ? (
            <p className="rc-empty__hint">No photos on file.</p>
          ) : null}
        </div>
      </section>

      <section className="rc-section">
        <h2 className="rc-section__title">Applicant answers</h2>
        <dl className="rc-facts">
          {answerRows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="rc-section">
        <h2 className="rc-section__title">Matching pool</h2>
        <p className="rc-lead" style={{ marginTop: 0 }}>
          {inPool ? (
            <>
              In pool · {matchingPoolStore.size()} profile
              {matchingPoolStore.size() === 1 ? "" : "s"} total
            </>
          ) : poolEligible ? (
            <>Not in pool yet — Approve will upsert if eligible.</>
          ) : (
            <>
              Approve will not add to pool until: {poolReasons.join("; ") || "checks fail"}.
            </>
          )}
        </p>
      </section>

      <section className="rc-section rc-decision">
        <h2 className="rc-section__title">Decision</h2>
        <label className="field">
          <span className="field__label">
            Reviewer note {pending ? "(required for Needs Resubmit)" : ""}
          </span>
          <textarea
            className="field__textarea"
            rows={3}
            maxLength={500}
            placeholder="e.g. ID corners cropped — please re-upload"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={!pending}
          />
        </label>

        {error ? <p className="rc-error">{error}</p> : null}

        {pending ? (
          <div className="rc-decision__actions">
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => decide("approved")}
              className="rc-btn-approve"
            >
              Approve
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => decide("rejected")}>
              Reject
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => decide("needs_resubmit")}>
              Needs Resubmit
            </Button>
          </div>
        ) : (
          <p className="rc-decision__done">
            This case is <strong>{v.status}</strong>
            {v.reviewNote ? ` — “${v.reviewNote}”` : ""}.
          </p>
        )}
      </section>
    </div>
  );
}
